import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AutomationTemplateListItem } from '@neuroedge/shared';
import { Button, Card, PageState, Select } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useAutomationTemplates } from '../../api/automation-templates';

const TRIGGER_LABEL: Record<string, string> = {
  CONVERSATION_CREATED: 'New conversation',
  NEW_MESSAGE: 'New message',
  FOLLOW_UP_DUE: 'Follow-up due',
  TASK_DUE: 'Task due',
};

const ACTION_LABEL: Record<string, string> = {
  SEND_WHATSAPP_TEMPLATE: 'Sends a template',
  SEND_TEXT: 'Sends a text reply',
  SEND_TEXT_WITH_BUTTONS: 'Sends quick-reply buttons',
  CREATE_LEAD: 'Creates a lead',
  START_FLOW: 'Starts a conversation flow',
};

const ALL_CATEGORIES = '__all__';

function TemplateCard({
  template,
  canUse,
}: {
  template: AutomationTemplateListItem;
  canUse: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Card>
      <div
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        <strong>{template.name}</strong>
        <span className="ne-badge">{template.category}</span>
        <span style={{ flex: 1, minWidth: 0 }} />
        {canUse ? (
          <Button onClick={() => navigate(`/automation-templates/${template.id}/use`)}>
            Use this template
          </Button>
        ) : null}
      </div>
      {template.description ? (
        <p style={{ color: 'var(--color-text-muted)', margin: 'var(--space-2) 0 0' }}>
          {template.description}
        </p>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-3)',
        }}
      >
        {template.triggerTypes.map((t) => (
          <span key={t} className="ne-badge">
            Trigger: {TRIGGER_LABEL[t] ?? t}
          </span>
        ))}
        {template.actionTypes.map((a, i) => (
          <span key={`${a}-${i}`} className="ne-badge">
            {ACTION_LABEL[a] ?? a}
          </span>
        ))}
      </div>
    </Card>
  );
}

/** Tenant-facing browse page for the master automation template library (docs/API_SPEC.md §14). */
export function AutomationTemplatesPage() {
  const { hasPermission } = useAuth();
  const canUse = hasPermission('automation.create');
  const [category, setCategory] = useState('');

  const { data, isPending, isError, error, refetch } = useAutomationTemplates({
    category: category || undefined,
  });

  const categories = useMemo(() => {
    const seen = new Set<string>();
    for (const t of data?.items ?? []) seen.add(t.category);
    return [...seen].sort();
  }, [data?.items]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Automation templates</h1>
        <span style={{ flex: 1 }} />
        <div style={{ minWidth: 200 }}>
          <Select
            label="Category"
            value={category || ALL_CATEGORIES}
            onChange={(e) => setCategory(e.target.value === ALL_CATEGORIES ? '' : e.target.value)}
            options={[
              { value: ALL_CATEGORIES, label: 'All categories' },
              ...categories.map((c) => ({ value: c, label: c })),
            ]}
          />
        </div>
      </div>

      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Ready-made automations for common use cases — copy one into your workspace, then edit its
        replies before turning it on.
      </p>

      {isPending ? (
        <PageState kind="loading" message="Loading templates…" />
      ) : isError ? (
        <PageState kind="error" message={error.message} onRetry={() => void refetch()} />
      ) : data.items.length === 0 ? (
        <PageState kind="empty" title="No templates" message="No templates match this filter." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {data.items.map((t) => (
            <TemplateCard key={t.id} template={t} canUse={canUse} />
          ))}
        </div>
      )}

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/automations">← All automations</Link>
      </p>
    </div>
  );
}
